import type { PasswordLeakCheckResponse, PasswordLeakTrigger } from '@sunflower-games/crown-password-leak-client';

import { postRequest } from '../api';
import { resolveFlag } from '../launchDarkly';

import { hidePasswordLeakPrompt, showPasswordLeakPrompt } from './passwordLeakPrompt';

export const PASSWORD_LEAK_CHECK_FLAG = 'password_leak_check_enabled';
export const PASSWORD_LEAK_CHECK_ROUTE = '/v2/user/password-check';

interface PendingCheck {
  uid: string;
  trigger: PasswordLeakTrigger;
  /** Undefined when hashing failed. Only the hash is kept: the password lives in the closure until hashing ends. */
  credentialsHash: Promise<string | undefined>;
}

let pending: PendingCheck | null = null;

/**
 * Report-only reCAPTCHA password leak check. Called right after a Firebase email/password sign-in or
 * sign-up; starts hashing in the background. The backend route needs the player row, so the POST waits
 * for that player's `user/login` (`runPendingPasswordLeakCheck`). Never throws, never blocks sign-in.
 */
export function queuePasswordLeakCheck(args: {
  uid: string;
  email: string;
  password: string;
  trigger: PasswordLeakTrigger;
}) {
  pending = null;
  try {
    if (!resolveFlag(PASSWORD_LEAK_CHECK_FLAG, false)) return;
    const { email, password } = args;
    // Lazy, so the hashing code is never downloaded while the flag is off.
    const credentialsHash = import('@sunflower-games/crown-password-leak-client')
      .then(({ passwordLeakCredentialsHash }) => passwordLeakCredentialsHash(email, password))
      .catch(() => undefined);
    pending = { uid: args.uid, trigger: args.trigger, credentialsHash };
  } catch {
    pending = null;
  }
}

async function sendPasswordLeakCheck({ trigger, credentialsHash }: PendingCheck) {
  const hash = await credentialsHash;
  if (!hash) return;
  // OX Agent: Sensitive Data Protection prevented — the hash goes only into this POST body, never a log or error
  const res = await postRequest<{ data?: PasswordLeakCheckResponse }>(
    PASSWORD_LEAK_CHECK_ROUTE,
    { credentialsHash: hash, trigger },
    // A rejected check must never sign the player out.
    { skipAuthLogout: true },
  );
  if (res?.data?.status === 'checked' && res.data.leaked) showPasswordLeakPrompt();
}

/** Fire-and-forget, after `user/login` returned a profile for `uid`. Swallows every error. */
export function runPendingPasswordLeakCheck(uid: string) {
  const check = pending;
  if (!check || check.uid !== uid) return;
  pending = null;
  sendPasswordLeakCheck(check).catch(() => undefined);
}

export function clearPasswordLeakCheck() {
  pending = null;
  hidePasswordLeakPrompt();
}
